import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MessageCircle, Check } from "lucide-react";
import { NAV_MENUS, WA_NUMBER, PRODUCTS } from "../data/site";
import { SERVICE_PAGES } from "../data/servicePages";
import { slugOf } from "../components/Navbar";
import { Reveal } from "../components/Reveal";
import LogoChip from "../components/LogoChip";
import ServiceDetail from "./ServiceDetail";
import SpeedDetectionPage from "./products/SpeedDetection";
import FaceRecognitionPage from "./products/FaceRecognition";
import PpeDetectionPage from "./products/PpeDetection";
import ProductDetail from "./products/ProductDetail";
import { PRODUCT_PAGES } from "../data/productPages";

const ProductStub = ({ product, openContact }) => (
  <main className="pt-36 pb-28 min-h-screen" data-testid={`product-page-${product.id}`}>
    <div className="max-w-4xl mx-auto px-6">
      <Reveal>
        <Link to="/products" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-slate-500 hover:text-smipl-orange transition-colors">
          <ArrowLeft size={13} /> Our Products
        </Link>
      </Reveal>
      <Reveal delay={0.08}>
        <div className="mt-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-smipl-orange/10 border border-smipl-orange/25 text-smipl-orange">
          <product.icon size={28} />
        </div>
        <p className="mt-6 font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange">Our Products</p>
        <h1 className="mt-3 font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.1]">{product.name}</h1>
        <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl">{product.blurb}</p>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-10 inline-block rounded-full border border-smipl-orange/30 bg-smipl-orange/10 px-4 py-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-smipl-orange">
          Detailed documentation & live demo available on request
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-wrap gap-4 border-t border-white/8 pt-10">
          <button data-testid="product-talk-btn" onClick={openContact} className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-7 py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300">
            Let's Talk <ArrowUpRight size={15} />
          </button>
          <button data-testid="product-whatsapp-btn" onClick={() => (WA_NUMBER ? window.open(`https://wa.me/${WA_NUMBER}`, "_blank") : openContact())} className="flex items-center gap-2 rounded-full border border-wa/50 text-wa px-7 py-3.5 text-sm font-semibold hover:bg-wa hover:text-navy-deep transition-all duration-300">
            <MessageCircle size={15} /> WhatsApp
          </button>
        </div>
      </Reveal>
    </div>
  </main>
);

const ItemPage = ({ openContact }) => {
  const { group, id } = useParams();
  if (group === "products") {
    const product = PRODUCTS.find((p) => p.id === id);
    if (!product) return <Navigate to="/products" replace />;
    if (id === "vehicle-speed-violation") return <SpeedDetectionPage openContact={openContact} />;
    if (id === "face-recognition") return <FaceRecognitionPage openContact={openContact} />;
    if (id === "ppe-detection") return <PpeDetectionPage openContact={openContact} />;
    if (PRODUCT_PAGES[id]) return <ProductDetail key={id} data={PRODUCT_PAGES[id]} openContact={openContact} />;
    return <ProductStub product={product} openContact={openContact} />;
  }
  const menu = NAV_MENUS.find((m) => slugOf(m.label) === group);
  const item = menu?.items.find((i) => i.id === id);
  if (!item || item.scroll) return <Navigate to={menu ? `/${group}` : "/"} replace />;
  const detail = SERVICE_PAGES[`${group}/${id}`];
  if (detail) return <ServiceDetail detail={detail} item={item} group={group} groupLabel={menu.label} openContact={openContact} />;

  return (
    <main className="pt-36 pb-28 min-h-screen" data-testid={`item-page-${id}`}>
      <div className="max-w-4xl mx-auto px-6">
        <Reveal>
          <Link
            to={`/${group}`}
            data-testid="item-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-slate-500 hover:text-smipl-orange transition-colors"
          >
            <ArrowLeft size={13} /> {menu.label}
          </Link>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-smipl-orange/10 border border-smipl-orange/25 text-smipl-orange">
            <item.icon size={28} />
          </div>
          <p className="mt-6 font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange">{menu.label}</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">{item.name}</h1>
          <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl">{item.blurb}</p>
        </Reveal>

        {item.capabilities?.length > 0 && (
          <Reveal delay={0.15}>
            <p className="mt-12 mb-5 font-mono text-[11px] tracking-[0.3em] uppercase text-slate-500">What this covers</p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5" data-testid="item-capabilities-list">
              {item.capabilities.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm md:text-base text-slate-300">
                  <Check size={15} className="mt-1 shrink-0 text-smipl-orange" /> {c}
                </li>
              ))}
            </ul>
          </Reveal>
        )}

        {item.partners?.length > 0 && (
          <Reveal delay={0.2}>
            <p className="mt-12 mb-5 font-mono text-[11px] tracking-[0.3em] uppercase text-slate-500">Partner Roster</p>
            <div className="flex flex-wrap gap-3" data-testid="item-partner-roster">
              {item.partners.map((p) => (
                <LogoChip key={p.name} name={p.name} domain={p.domain} />
              ))}
            </div>
          </Reveal>
        )}

        {item.note && (
          <Reveal delay={0.22}>
            <span className="mt-10 inline-block rounded-full border border-smipl-orange/30 bg-smipl-orange/10 px-4 py-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-smipl-orange">
              {item.note}
            </span>
          </Reveal>
        )}

        <Reveal delay={0.25}>
          <div className="mt-14 flex flex-wrap gap-4 border-t border-white/8 pt-10">
            <button
              data-testid="item-talk-btn"
              onClick={openContact}
              className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-7 py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300"
            >
              Start a Conversation <ArrowUpRight size={15} />
            </button>
            <button
              data-testid="item-whatsapp-btn"
              onClick={() => (WA_NUMBER ? window.open(`https://wa.me/${WA_NUMBER}`, "_blank") : openContact())}
              className="flex items-center gap-2 rounded-full border border-wa/50 text-wa px-7 py-3.5 text-sm font-semibold hover:bg-wa hover:text-navy-deep transition-all duration-300"
            >
              <MessageCircle size={15} /> WhatsApp
            </button>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default ItemPage;
